import {useCallback, useEffect, useRef, useState} from 'react';
import {createUseStyles} from 'react-jss';
import {connect} from 'react-redux';
import Button from '../../../../../UI/Button/Button';
import {
  postcodeAnywhereShowFields,
  resetEditedAddress,
  updateDeliveryAddressFieldInOverlay, updateEditedAddress,
} from '../../../../actions/address-actions';
import editDeleteAddressActions from '../../../../actions/addressActions/editDeleteAddressActions';
import {hideOverlay, showOverlay} from '../../../../actions/overlay-actions';
import FieldContainer2 from '../../../../form/FieldContainer2';
import Translation from '../../../../lang/Translation';
import getEditedAddressUuid from '../../../../selectors/DeliveryAddressSelectors/getEditedAddressUuid';
import {isMobile} from '../../../../selectors/DeviceSelectors';
import {
  getSelectedDeliveryCountry,
} from '../../../../selectors/selectors';
import Spacing from '../../../../style-helper/Spacing';
import V2Components from '../../../../v2-components';
import addressDefinitionsByCountry from '../../../address/address-definitions-by-country';
import AddressFields from '../../../address/AddressFields';
import {TooltipAndOverlayIDs} from '../../../detailed-tooltip/TooltipLibrary';
import CheckoutOverlay from '../../CheckoutOverlay';

const useStyles = createUseStyles({
  contentWrapper: {
    padding: [Spacing.single, Spacing.double, Spacing.triple],
  },
  saveButton: {
    margin:[Spacing.double, 0, Spacing.double],
  }
});

export function EditAddressOverlay(props) {
  const classes = useStyles();
  
  const fieldContainerRef = useRef(null);
  
  const initialAddress = props.fieldValues;
  
  const fieldsToShow = addressDefinitionsByCountry.get(props.deliveryCountry)
    .filter(field => !field.filterCondition || field.filterCondition({addressType: 'delivery'}));
  
  const prefixErrors = errors => errors?.map(error => ({
    fieldName: `editAddressForm.${error.fieldName}`,
    errorCode: error.errorCode,
  }));
  
  const validate = useCallback(
    () => {
      if (!fieldContainerRef.current) return {isValid: true};
      const containerValidationResult = fieldContainerRef.current.validate();
      return {
        isValid: containerValidationResult.isValid,
        errors: prefixErrors(containerValidationResult.errors),
      };
    }, [fieldContainerRef],
  );
  
  const handleEditAddressClick = () => {
    if (validate().isValid) {
      props.editAddress(props.editedFieldValues[props.addressId], props.deliveryCountry);
      props.removeManualPCAEntry();
    }
  };
  
  const handleCancelEditAddress = () => {
    props.resetEditedAddress();
    props.removeManualPCAEntry();
    if (props.isMobile) {
      props.showOverlay(TooltipAndOverlayIDs.EDIT_DELETE_ADDRESS, props)
    } else props.hideOverlay()
  }
  
  useEffect(() => {
    props.initialiseEditAddress(props.fieldValues);
  }, [])
  
  useEffect(() => {
    V2Components.register('EditAddressForm', {validate});
    return () => V2Components.clear('EditAddressForm');
  }, [validate]);
  
  return (
    <CheckoutOverlay
      popup
      stickyScaleToContentMobile
      ordersummary
      nopadding
      minheight
      title="checkout.delivery.editAddress.overlay.title"
      exitCallback={props.resetEditedAddress}
    >
      <div className={classes.contentWrapper} id="EditAddressOverlay__form">
        <FieldContainer2 fieldUpdateDispatcher={props.updateFormField}>
          {(fieldContainer) => {
            fieldContainerRef.current = fieldContainer;
            return (
              <AddressFields
                definedFields={fieldsToShow}
                addressType="delivery"
                fieldContainer={fieldContainer}
                fieldContainerPhone={fieldContainer}
                fieldNamePrefix="editAddressForm."
                fieldValues={props.editedFieldValues[props.addressId] ?
                  props.editedFieldValues[props.addressId] : props.fieldValues}
                resetAddress={props.resetAddress}
              />
            );
          }}
        </FieldContainer2>
        <Button
          primary
          fullWidth={!props.isMobile}
          id="saveEditAddress"
          onClick={handleEditAddressClick}
          disabled={JSON.stringify(initialAddress) === JSON.stringify(props.editedFieldValues[props.addressId])}
          className={classes.saveButton}
        >
          <Translation>btn.save.text</Translation>
        </Button>
        <Button
          secondary
          fullWidth={!props.isMobile}
          id="cancelEditAddress"
          onClick={handleCancelEditAddress}
        >
          <Translation>btn.cancel.text</Translation>
        </Button>
      </div>
    </CheckoutOverlay>
  );
}

const mapStateToProps = (state, ownProps) => {
  let addressValues = state.address.deliveryAddressList.addresses[ownProps.addressId];
  let addressUuid = getEditedAddressUuid(state);
  return {
    addressUuid: getEditedAddressUuid(state),
    fieldValues: {...addressValues, ...addressUuid},
    editedFieldValues: state.address.deliveryAddressList.editedAddress,
    deliveryCountry: getSelectedDeliveryCountry(state),
    isMobile: isMobile(state)
  }
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateFormField: (fieldName, value) => {
    dispatch(updateDeliveryAddressFieldInOverlay({addressId: ownProps.addressId}, fieldName, value));
  },
  initialiseEditAddress: (fieldValues) => {
    dispatch(updateEditedAddress(ownProps.addressId, fieldValues));
  },
  resetAddress: (fieldValues) => {
    dispatch(updateEditedAddress(ownProps.addressId, fieldValues));
  },
  editAddress: (fieldValues, deliveryCountry) => {
    dispatch(editDeleteAddressActions.editAddress(fieldValues, deliveryCountry))
  },
  resetEditedAddress: () => {
    dispatch(resetEditedAddress());
  },
  showOverlay: () => {
    dispatch(showOverlay())
  },
  hideOverlay: () => {
    dispatch(hideOverlay())
  },
  removeManualPCAEntry: () => {
    dispatch(postcodeAnywhereShowFields('delivery', false))
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(EditAddressOverlay);
